import { createContext, useContext, useState, useEffect } from 'react';
import { Email, EmailTemplate, EmailStats } from '@/types/email';
import { mockTemplates } from '@/data/mockEmails';
import { GmailService } from '@/lib/gmail';
import { useToast } from '@/hooks/use-toast';

interface EmailContextType {
  sentEmails: Email[];
  templates: EmailTemplate[];
  isGmailConnected: boolean;
  connectedEmail: string | null;
  connectGmail: () => void;
  disconnectGmail: () => void;
  sendEmail: (email: Omit<Email, 'id' | 'sentAt' | 'stats'>) => void;
  getEmailStats: (emailId: string) => EmailStats | undefined;
  createTemplate: (template: Omit<EmailTemplate, 'id'>) => void;
  updateTemplate: (id: string, data: Partial<EmailTemplate>) => void;
  deleteTemplate: (id: string) => void;
}

const EmailContext = createContext<EmailContextType | undefined>(undefined);

export const EmailProvider = ({ children }: { children: React.ReactNode }) => {
  const [sentEmails, setSentEmails] = useState<Email[]>([]);
  const [templates, setTemplates] = useState<EmailTemplate[]>(mockTemplates);
  const [isGmailConnected, setIsGmailConnected] = useState(false);
  const [connectedEmail, setConnectedEmail] = useState<string | null>(null);
  const { toast } = useToast();
  const gmailService = GmailService.getInstance();

  // Initialize Gmail service and check connection status

  useEffect(() => {
    const initializeGmail = async () => {
      try {
        if (!import.meta.env.VITE_GMAIL_API_KEY || !import.meta.env.VITE_GMAIL_CLIENT_ID) {
          throw new Error('Gmail configuration missing. Please check your environment variables.');
        }
        await gmailService.init();
        const connected = gmailService.isConnected();
        setIsGmailConnected(connected);
        if (connected) {
          const email = gmailService.getConnectedEmail();
          setConnectedEmail(email);
        }
      } catch (error) {
        let errorMessage = error instanceof Error ? error.message : 'Unknown error';
        if (error && typeof error === 'object' && 'error' in error) {
          const apiError = (error as any).error;
          errorMessage = apiError.message || errorMessage;
        }
        // console.error('Gmail initialization error:', errorMessage);
      }
    };
    
    initializeGmail();
  }, [toast]);

  const connectGmail = () => {
    try {
      gmailService.signIn()
        .then(() => {
          setIsGmailConnected(true);
          const email = gmailService.getConnectedEmail();
          setConnectedEmail(email);
          toast({
            title: 'Gmail Connected',
            description: `Successfully connected to ${email}`,
          });
        })
        .catch((error) => {
          let errorMessage = error instanceof Error ? error.message : 'Unknown error';
          if (error && typeof error === 'object' && 'error' in error) {
            const apiError = (error as any).error;
            errorMessage = apiError.message || errorMessage;
          }
          // console.error('Gmail authentication failed:', errorMessage);
          toast({
            title: 'Gmail Connection Failed',
            description: errorMessage,
            variant: 'destructive',
          });
        });
    } catch (error) {
      let errorMessage = error instanceof Error ? error.message : 'Unknown error';
      if (error && typeof error === 'object' && 'error' in error) {
        const apiError = (error as any).error;
        errorMessage = apiError.message || errorMessage;
      }
      // console.error('Gmail authentication failed:', errorMessage);
      toast({
        title: 'Gmail Connection Failed',
        description: errorMessage,
        variant: 'destructive',
      });
    }
  };

  const disconnectGmail = async () => {
    try {
      await gmailService.signOut();
      setIsGmailConnected(false);
      setConnectedEmail(null);
      toast({
        title: 'Gmail Disconnected',
        description: 'Successfully disconnected from Gmail.',
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to disconnect from Gmail.',
        variant: 'destructive',
      });
    }
  };

  const sendEmail = async (email: Omit<Email, 'id' | 'sentAt' | 'stats'>) => {
    // In a real app, we would call an email API here
    try {
      await gmailService.sendEmail(
        email.recipientEmail,
        email.subject,
        email.content
      );
    } catch (error) {
      // console.error('Failed to send email:', error);
    }

    const newEmail: Email = {
      ...email,
      id: Date.now().toString(),
      sentAt: new Date().toISOString(),
      stats: {
        opened: false,
        openedAt: null,
        clicked: false,
        clickedAt: null,
        replied: false,
        repliedAt: null,
      },
    };
    
    setSentEmails((prev) => [...prev, newEmail]);
    
    // This would be handled by a webhook in a real app
    setTimeout(() => {
      setSentEmails((prev) =>
        prev.map((e) => {
          if (e.id === newEmail.id) {
            return {
              ...e,
              stats: {
                ...e.stats,
                opened: Math.random() > 0.3,
                openedAt: Math.random() > 0.3 ? new Date(Date.now() + 1000 * 60 * 5).toISOString() : null,
              },
            };
          }
          return e;
        })
      );
    }, 5000);
  };

  const getEmailStats = (emailId: string) => {
    const email = sentEmails.find((e) => e.id === emailId);
    return email?.stats;
  };

  const createTemplate = (template: Omit<EmailTemplate, 'id'>) => {
    const newTemplate: EmailTemplate = {
      ...template,
      id: Date.now().toString(),
    };
    setTemplates((prev) => [...prev, newTemplate]);
  };

  const updateTemplate = (id: string, data: Partial<EmailTemplate>) => {
    setTemplates((prev) =>
      prev.map((template) =>
        template.id === id ? { ...template, ...data } : template
      )
    );
  };

  const deleteTemplate = (id: string) => {
    setTemplates((prev) => prev.filter((template) => template.id !== id));
  };

  return (
    <EmailContext.Provider
      value={{
        sentEmails,
        templates,
        isGmailConnected,
        connectedEmail,
        connectGmail,
        disconnectGmail,
        sendEmail,
        getEmailStats,
        createTemplate,
        updateTemplate,
        deleteTemplate,
      }}
    >
      {children}
    </EmailContext.Provider>
  );
};

export const useEmails = () => {
  const context = useContext(EmailContext);
  if (context === undefined) {
    throw new Error('useEmails must be used within an EmailProvider');
  }
  return context;
};